import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { Card, Button, Typography, Modal, App } from 'antd'
import { useSelectedImage } from '../hooks/useSelectedImage'
import { useAIRecognition } from '../context/AIRecognitionContext'
import { useSensorTracking } from '../hooks/useSensorTracking'
import {
  XC_RECOGNITION_STATUS,
  XCSafetyScenario,
  XCSelectionInfo
} from '../types'
import upload_icon from '../../../images/verify-auth/upload_icon.png'
import { useAppSelector } from '../../../app/hooks'
import ContactUS from '../../components/ContactUS'
import ImageSelectionEditor, {
  XCImageSelectionEditorRef
} from '../../components/ImageSelectionEditor'
import useImage from 'use-image'
import { calculateImageDisplaySize } from '../../../utils/imageUtils'
import { ThunderboltOutlined } from '@ant-design/icons'

/**
 * 图片上传区域组件的 Props 接口
 */
interface XCSelectImageAreaProps {
  onShowSafetyScenarioSelection?: (selectionInfo: XCSelectionInfo[]) => void
}

/**
 * 图片上传区域组件
 */
const SelectImageArea: React.FC<XCSelectImageAreaProps> = ({
  onShowSafetyScenarioSelection
}) => {
  const { message, modal } = App.useApp()
  const { selectedImages, handleSelectPhotos, handleRecognition } =
    useSelectedImage()
  const {
    recognitionStatus,
    setSelectedImages,
    setSelectionInfo,
    selectionInfo,
    setImageSelectionEditorRef,
    userId,
    selectedSafetyScenarios,
    rememberChoice
  } = useAIRecognition()

  const selectedPhotos = useAppSelector((state) => state.photoPicker.photos)

  // ImageSelectionEditor 的引用
  const imageSelectionEditorRef = useRef<XCImageSelectionEditorRef>(null)
  // recognitionStatus 的 ref，避免闭包陷阱
  const recognitionStatusRef = useRef(recognitionStatus)
  const selectionInfoRef = useRef<XCSelectionInfo[] | undefined>(selectionInfo)
  const selectedImagesRef = useRef<string[]>(selectedImages)
  const rememberChoiceRef = useRef(rememberChoice)
  const { reportAISafetyHazardsClick } = useSensorTracking()

  // 同步更新 recognitionStatusRef
  useEffect(() => {
    recognitionStatusRef.current = recognitionStatus
  }, [recognitionStatus])

  // 保持 ref 与状态同步
  useEffect(() => {
    selectionInfoRef.current = selectionInfo
  }, [selectionInfo])

  useEffect(() => {
    selectedImagesRef.current = selectedImages
  }, [selectedImages])

  useEffect(() => {
    rememberChoiceRef.current = rememberChoice
  }, [rememberChoice])

  const [image] = useImage(selectedImages[0])

  // 容器宽度
  const containerWidth = 400

  // 计算容器高度，基于图片宽高比
  const calculateContainerHeight = useCallback(() => {
    if (!image) return 450 // 默认高度
    const padding = 20 // 容器内边距

    // 计算图片显示尺寸
    let displayWidth = Math.min(image.width, containerWidth)
    let displayHeight = (image.height / image.width) * displayWidth

    // 容器高度 = 图片高度 + 内边距
    return Math.max(displayHeight + padding * 2, 200) // 最小高度200px
  }, [image, recognitionStatus, selectedImages.length])

  // 计算图片显示尺寸
  const calculateImageSize = useCallback(() => {
    if (!image) return { width: containerWidth, height: 450 }

    const maxWidth = containerWidth

    return calculateImageDisplaySize(
      { width: image.width, height: image.height },
      maxWidth,
      window.innerHeight - 300
    )
  }, [image, recognitionStatus, selectedImages.length])

  const containerHeight = calculateContainerHeight()
  const imageSize = calculateImageSize()

  // 选择照片后，自动调用识别
  useEffect(() => {
    console.log('selectedPhotos', selectedPhotos)
    if (selectedPhotos.length > 0) {
      console.log('selectedPhotos', JSON.stringify(selectedPhotos))
      const images = selectedPhotos.map((photo) => photo.largeURL)
      setSelectedImages(images)
      setSelectionInfo([])
    }
  }, [selectedPhotos, setSelectedImages])

  // 设置 ImageSelectionEditor 引用到 context 中
  useEffect(() => {
    setImageSelectionEditorRef(imageSelectionEditorRef)
  }, [setImageSelectionEditorRef, imageSelectionEditorRef])

  /**
   * 处理批量选择
   */
  const handleBatchSelect = useCallback(() => {
    // 上报AI安全隐患识别埋点
    reportAISafetyHazardsClick({ clickItem: 'batchChoose' })

    console.log('批量选择功能暂不支持')
    modal.confirm({
      title: '功能还在紧张开发中\n联系客服了解功能上线进度',
      content: '如有需要可点击下方催促开发',
      okText: '联系客服',
      onOk: () => {
        // 上报埋点
        reportAISafetyHazardsClick({
          clickItem: 'popContactUs'
        })
        // 模拟点击联系客服
        const contactTrigger = document.querySelector('.contact-us-trigger')
        if (contactTrigger) {
          ;(contactTrigger as HTMLElement).click()
        }
      },
      onCancel: () =>
        reportAISafetyHazardsClick({
          clickItem: 'popCancel'
        })
    })
  }, [reportAISafetyHazardsClick])

  /**
   * 渲染上传区域
   */
  const renderUploadArea = useCallback(
    () => (
      <div style={{ alignItems: 'center' }}>
        <div
          style={{
            background: '#fafafa',
            border: '2px dashed #d9d9d9',
            borderRadius: '6px',
            textAlign: 'center',
            minHeight: '380px',
            aspectRatio: '1',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}
        >
          <div
            style={{
              flexDirection: 'column',
              justifyContent: 'flex-start'
            }}
          >
            <img
              src={upload_icon}
              alt=""
              style={{
                width: '64px',
                height: '58px',
                marginBottom: 20
              }}
            />
            <p
              style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#1E1E1E',
                lineHeight: '20px'
              }}
            >
              请先选择照片
            </p>
            <p
              style={{
                fontSize: '14px',
                color: '#1E1E1E'
              }}
            >
              <ThunderboltOutlined style={{ width: '12px', height: '12px' }} />
              减少80%重复劳动，管理员效率翻倍
            </p>

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '12px',
                marginTop: 24
              }}
            >
              <Button
                type="primary"
                onClick={() => {
                  // 上报选择照片埋点
                  reportAISafetyHazardsClick({ clickItem: 'choosePhoto' })
                  handleSelectPhotos()
                }}
                className="common-button"
              >
                选择照片
              </Button>
              <Button onClick={handleBatchSelect} className="common-button">
                批量选择
              </Button>
            </div>
          </div>
        </div>
      </div>
    ),
    [
      handleSelectPhotos,
      handleBatchSelect,
      containerHeight,
      imageSize,
      selectedImages
    ]
  )

  /**
   * 处理选择区域的回调函数
   * @param selectionInfo
   */
  const onSelectionChange = (selectionInfo: XCSelectionInfo[]) => {
    console.log('onSelectionChange', selectionInfo)
    reportAISafetyHazardsClick({ clickItem: 'changeImageSelection' })

    if (selectionInfo.length > 0) {
      setSelectionInfo(selectionInfo)
      selectionInfoRef.current = selectionInfo
    }
  }

  // 处理点击开始识别的回调函数
  const handleConfirm = useCallback(
    (selectionInfo: XCSelectionInfo[]) => {
      if (selectionInfo.length === 0) {
        message.error('请选择图片识别区域')
        return
      }

      if (rememberChoiceRef.current) {
        const processedScenarios = selectedSafetyScenarios.map((scenario) => {
          // 检查是否是自定义选项
          if (scenario.id === '__custom__') {
            const customContent =
              (scenario as any).customContent || scenario.description || ''
            console.log(
              'Processing custom scenario in SelectImageArea:',
              scenario,
              'customContent:',
              customContent
            )
            return {
              id: '__custom__',
              name: '自定义',
              description: customContent,
              selected: true
            } as XCSafetyScenario
          }
          return scenario
        })

        handleRecognition(
          selectedImagesRef.current,
          userId ?? '',
          selectionInfoRef.current ?? [],
          processedScenarios
        )
        return
      }

      // 直接传递 selectionInfo 参数，避免异步状态更新导致的时序问题
      if (onShowSafetyScenarioSelection) {
        onShowSafetyScenarioSelection(selectionInfoRef.current ?? [])
      }
    },
    [setSelectionInfo, onShowSafetyScenarioSelection]
  )

  /**
   * 判断是否显示操作按钮
   */
  const shouldShowActionButtons = useCallback(() => {
    return (
      selectedImages.length > 0 &&
      recognitionStatus !== XC_RECOGNITION_STATUS.RECOGNIZING
    )
  }, [selectedImages.length, recognitionStatus])

  /**
   * 渲染主要操作按钮
   */
  const renderPrimaryActionButton = useCallback(() => {
    const isCompleted = recognitionStatus === XC_RECOGNITION_STATUS.COMPLETED
    const isWaiting = recognitionStatus === XC_RECOGNITION_STATUS.WAITING

    const handleClick = () => {
      if (isCompleted) {
        reportAISafetyHazardsClick({ clickItem: 'chooseAgain' })
        handleSelectPhotos()
      } else {
        reportAISafetyHazardsClick({
          clickItem: isWaiting
            ? 'waitStartRecognition'
            : 'completeStartRecognition'
        })
        handleConfirm(selectionInfoRef.current ?? [])
      }
    }

    return (
      <Button
        type="primary"
        onClick={handleClick}
        className="common-button"
        style={{
          borderRadius: '8px',
          margin: '16px 0',
          width: isWaiting ? 370 : undefined
        }}
      >
        {isCompleted ? '选择照片' : '开始识别'}
      </Button>
    )
  }, [
    recognitionStatus,
    reportAISafetyHazardsClick,
    handleSelectPhotos,
    handleConfirm
  ])

  /**
   * 渲染次要操作按钮（重新识别）
   */
  const renderSecondaryActionButton = useCallback(() => {
    if (recognitionStatus !== XC_RECOGNITION_STATUS.COMPLETED) {
      return null
    }

    return (
      <Button
        type="primary"
        onClick={() => {
          reportAISafetyHazardsClick({ clickItem: 'reRecognition' })
          handleConfirm(selectionInfoRef.current ?? [])
        }}
        className="common-button"
        style={{
          borderRadius: '8px',
          margin: '16px 0'
        }}
      >
        重新识别
      </Button>
    )
  }, [recognitionStatus, reportAISafetyHazardsClick, handleConfirm])

  const renderUploadedImages = useCallback(
    () => (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '8px',
          backgroundColor: 'red'
        }}
      >
        {/* 背景图片区域 */}
        <div
          style={{
            width: imageSize.width,
            height: imageSize.height,
            background: '#C1C1C1',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          {/* 图片展示组件 */}
          {selectedImages.length && (
            <ImageSelectionEditor
              ref={imageSelectionEditorRef}
              src={selectedImages[0]}
              width={imageSize.width}
              height={imageSize.height}
              hasSelection={Boolean(selectionInfo?.length)}
              onSelectionChange={onSelectionChange}
            />
          )}
        </div>
      </div>
    ),
    [
      selectedImages,
      recognitionStatus,
      handleSelectPhotos,
      handleBatchSelect,
      containerHeight,
      imageSize
    ]
  )

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Card
        style={{
          flex: 1,
          boxShadow: '-2px -1px 13.6px 0px rgba(233, 233, 233, 1)',
          borderRadius: 10,
          borderColor: '#ffffff',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        {selectedImages.length === 0
          ? renderUploadArea()
          : renderUploadedImages()}
      </Card>
      {shouldShowActionButtons() && (
        <div
          style={{ flex: 1, display: 'flex', flexDirection: 'row', gap: 12 }}
        >
          {renderPrimaryActionButton()}
          {renderSecondaryActionButton()}
        </div>
      )}

      {/* 隐藏的联系客服组件，用于批量选择功能 */}
      <ContactUS fromPage="AI识别批量选择">
        <div className="contact-us-trigger" style={{ display: 'none' }} />
      </ContactUS>
    </div>
  )
}

export default SelectImageArea
